<script setup>
import { toast } from 'dashboard/composables/useToast';
import { usePermissions } from 'dashboard/composables/usePermissions';
import { computed, ref } from 'vue';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import SettingsPageLayout from 'dashboard/components-next/Settings/SettingsPageLayout.vue';
import SettingsTable from 'dashboard/components-next/Settings/SettingsTable.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();
const getters = useStoreGetters();
const teamsList = computed(() => getters['teams/getTeams'].value);
const uiFlags = computed(() => getters['teams/getUIFlags'].value);
const { canManageTeams } = usePermissions();

const loading = ref({});

const tableHeaders = computed(() => [
  t('TEAMS_SETTINGS.LIST.TABLE_HEADER.NAME'),
  '',
]);

const deleteTeam = async ({ id }) => {
  try {
    loading.value[id] = true;
    await store.dispatch('teams/delete', id);
    toast.success(t('TEAMS_SETTINGS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    toast.error(t('TEAMS_SETTINGS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[id] = false;
  }
};

const showDeletePopup = ref(false);
const selectedTeam = ref({});

const openDelete = team => {
  showDeletePopup.value = true;
  selectedTeam.value = team;
};

const closeDelete = () => {
  showDeletePopup.value = false;
  selectedTeam.value = {};
};

const confirmDeletion = () => {
  deleteTeam(selectedTeam.value);
  closeDelete();
};

const deleteConfirmText = computed(
  () => `${t('TEAMS_SETTINGS.DELETE.CONFIRM.YES')} ${selectedTeam.value.name}`
);

const deleteRejectText = computed(() => t('TEAMS_SETTINGS.DELETE.CONFIRM.NO'));

const confirmDeleteTitle = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.TITLE', {
    teamName: selectedTeam.value.name,
  })
);

const confirmPlaceHolderText = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.PLACE_HOLDER', {
    teamName: selectedTeam.value.name,
  })
);
</script>

<template>
  <SettingsPageLayout
    :title="$t('TEAMS_SETTINGS.HEADER')"
    :description="$t('TEAMS_SETTINGS.DESCRIPTION')"
  >
    <template #actions>
      <router-link v-if="canManageTeams" :to="{ name: 'settings_teams_new' }">
        <Button
          icon="i-lucide-circle-plus"
          :label="$t('TEAMS_SETTINGS.NEW_TEAM')"
        />
      </router-link>
    </template>

    <SettingsTable
      :headers="tableHeaders"
      :is-loading="uiFlags.isFetching"
      :is-empty="!teamsList.length"
      :empty-message="$t('TEAMS_SETTINGS.LIST.404')"
    >
      <tr
        v-for="team in teamsList"
        :key="team.id"
        class="hover:bg-n-solid-3 transition-colors"
      >
        <td class="px-5 py-3">
          <span class="block text-sm font-medium text-n-slate-12 capitalize">
            {{ team.name }}
          </span>
          <p class="mb-0 text-xs text-n-slate-10 mt-0.5">
            {{ team.description }}
          </p>
        </td>
        <td class="px-5 py-3">
          <div class="flex justify-end gap-1">
            <router-link
              :to="{
                name: 'settings_teams_edit',
                params: { teamId: team.id },
              }"
            >
              <Button
                v-if="canManageTeams"
                v-tooltip.top="$t('TEAMS_SETTINGS.LIST.EDIT_TEAM')"
                icon="i-lucide-settings"
                slate
                xs
                faded
              />
            </router-link>
            <Button
              v-if="canManageTeams"
              v-tooltip.top="$t('TEAMS_SETTINGS.DELETE.BUTTON_TEXT')"
              icon="i-lucide-trash-2"
              xs
              ruby
              faded
              :is-loading="loading[team.id]"
              @click="openDelete(team)"
            />
          </div>
        </td>
      </tr>
    </SettingsTable>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="confirmDeleteTitle"
      :message="$t('TEAMS_SETTINGS.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedTeam.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsPageLayout>
</template>
